import React from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { UnitConversion, UnitConversionCreate, UnitConversionUpdate } from "@/app/types/inventory";
import { Unit } from "@/app/types/unit";

// Form schema using zod
const formSchema = z.object({
  from_unit_id: z.number().min(1, "From unit is required"),
  to_unit_id: z.number().min(1, "To unit is required"),
  conversion_factor: z.string().min(1, "Conversion factor is required")
    .refine(
      (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
      "Must be a positive number"
    ),
}).refine(
  (data) => data.from_unit_id !== data.to_unit_id,
  {
    message: "From and To units must be different",
    path: ["to_unit_id"], // Attach the error to the to_unit_id field
  }
);

interface UnitConversionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: UnitConversionCreate | UnitConversionUpdate) => void;
  defaultValues?: Partial<UnitConversion>;
  isLoading?: boolean;
  mode: "create" | "edit";
  units: Unit[];
}

const UnitConversionForm: React.FC<UnitConversionFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  units,
}) => {
  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      from_unit_id: defaultValues?.from_unit_id || 0,
      to_unit_id: defaultValues?.to_unit_id || 0,
      conversion_factor: defaultValues?.conversion_factor?.toString() || "",
    },
  });

  // Handle form submission
  const handleSubmit = form.handleSubmit((data) => {
    const formattedData = {
      ...data,
      conversion_factor: parseFloat(data.conversion_factor),
    };

    if (mode === "create") {
      onSubmit(formattedData as UnitConversionCreate);
    } else {
      // For edit, only send conversion_factor, as API doesn't allow changing the units
      onSubmit({ conversion_factor: formattedData.conversion_factor } as UnitConversionUpdate);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Unit Conversion" : "Edit Unit Conversion"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create" 
              ? "Define a new rule for converting between units (e.g., 1 Box = 10 Pcs)."
              : "Update the conversion factor between units."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* From Unit */}
            <FormField
              control={form.control}
              name="from_unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>From Unit*</FormLabel>
                  <Select
                    disabled={mode === "edit"} // Cannot change units in edit mode
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id.toString()}>
                          {unit.name} ({unit.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {mode === "edit" && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Units cannot be changed after creation.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* To Unit */}
            <FormField
              control={form.control}
              name="to_unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>To Unit*</FormLabel>
                  <Select
                    disabled={mode === "edit"} // Cannot change units in edit mode
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id.toString()}>
                          {unit.name} ({unit.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Conversion Factor */}
            <FormField
              control={form.control}
              name="conversion_factor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conversion Factor*</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.001"
                      min="0.001"
                      placeholder="e.g., 10.0"
                    />
                  </FormControl>
                  <p className="text-sm text-muted-foreground mt-1">
                    {mode === "create" && form.watch("from_unit_id") && form.watch("to_unit_id") && (
                      <>
                        1 {units.find(u => u.id === form.watch("from_unit_id"))?.name || "unit"} = {field.value || "?"} {units.find(u => u.id === form.watch("to_unit_id"))?.name || "unit(s)"}
                      </>
                    )}
                    {mode === "edit" && defaultValues?.from_unit && defaultValues.to_unit && (
                      <>
                        1 {defaultValues.from_unit.name} = {field.value || "?"} {defaultValues.to_unit.name}(s)
                      </>
                    )}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === "create" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  <>{mode === "create" ? "Create Conversion" : "Update Conversion"}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnitConversionForm; 